// Symbol → icon resolver for the Live Spreads table.
// • Crypto  · atomiclabs cryptocurrency-icons via jsDelivr CDN (400+ free SVGs)
// • Stocks  · Clearbit logo API (free, no auth)
// • Forex   · 2-flag pair via flagcdn.com
// • Indices · single country flag via flagcdn.com
// • Metals/Energies · inline gradient SVG (handled in SymbolIcon component)
//
// Every resolver returns a descriptor that the <SymbolIcon /> component renders.
// Image errors gracefully fall back to a lucide icon inside <SymbolIcon />.

// --- Currency code → ISO 3166-1 alpha-2 country code ------------------------
// Note: the EU uses the HatScripts alias "european_union" because the
// short "eu.svg" file in their repo is just a text redirect.
export const FX_CODE_TO_FLAG = {
  USD: "us", EUR: "european_union", GBP: "gb", JPY: "jp", CHF: "ch", AUD: "au", CAD: "ca",
  NZD: "nz", TRY: "tr", ZAR: "za", MXN: "mx", PLN: "pl", SEK: "se", NOK: "no",
  HKD: "hk", SGD: "sg", CNH: "cn", CNY: "cn", DKK: "dk", CZK: "cz", HUF: "hu",
  ILS: "il", THB: "th",
};

// --- Index symbol → country code -------------------------------------------
// Hub naming: WS30, SP500, NAS100, DAX40, CAC40, AEX25, AUS200, ESX50, ESP35,
// HSI50, JPN225, SMI20, UK100.  Legacy aliases kept for compatibility.
export const INDEX_FLAG = {
  WS30: "us", SP500: "us", NAS100: "us", US30: "us", SPX500: "us", RUS2K: "us", VIX: "us",
  DAX40: "de", GER40: "de",
  UK100: "gb",
  CAC40: "fr", FRA40: "fr",
  ESP35: "es", ITA40: "it",
  ESX50: "european_union", EU50: "european_union",
  SMI20: "ch", SWI20: "ch",
  AEX25: "nl", NETH25: "nl",
  JPN225: "jp",
  HSI50: "hk", HK50: "hk", CHINA50: "cn",
  AUS200: "au", IND50: "in", SA40: "za",
};

// --- Stock ticker → company domain for Clearbit logo API -------------------
// US (.US suffix stripped) → primary corporate domain
export const STOCK_DOMAINS = {
  // US tech & internet
  AAPL: "apple.com", MSFT: "microsoft.com", GOOGL: "abc.xyz", GOOG: "abc.xyz",
  AMZN: "amazon.com", META: "meta.com", TSLA: "tesla.com", NVDA: "nvidia.com",
  NFLX: "netflix.com", AMD: "amd.com", INTC: "intel.com", CRM: "salesforce.com",
  ORCL: "oracle.com", IBM: "ibm.com", CSCO: "cisco.com", ADBE: "adobe.com",
  PYPL: "paypal.com", SQ: "block.xyz", SHOP: "shopify.com", UBER: "uber.com",
  LYFT: "lyft.com", ABNB: "airbnb.com",
  // US consumer & retail
  DIS: "disney.com", NKE: "nike.com", MCD: "mcdonalds.com", KO: "coca-cola.com",
  PEP: "pepsico.com", WMT: "walmart.com", COST: "costco.com",
  HD: "homedepot.com", LOW: "lowes.com", TGT: "target.com",
  // US financials
  JPM: "jpmorganchase.com", BAC: "bankofamerica.com", WFC: "wellsfargo.com",
  C: "citigroup.com", GS: "goldmansachs.com", MS: "morganstanley.com",
  V: "visa.com", MA: "mastercard.com", "BRK.B": "berkshirehathaway.com",
  // US health & pharma
  JNJ: "jnj.com", PFE: "pfizer.com", MRK: "merck.com", LLY: "lilly.com",
  ABBV: "abbvie.com", UNH: "unitedhealthgroup.com",
  // US energy / industrial / telco
  CVX: "chevron.com", XOM: "exxonmobil.com", BA: "boeing.com", F: "ford.com",
  GM: "gm.com", T: "att.com", VZ: "verizon.com", PG: "pg.com",
  CAT: "caterpillar.com", HON: "honeywell.com",

  // EU
  "ASML.NL": "asml.com", "SAP.DE": "sap.com", "LVMH.FR": "lvmh.com",
  "NESN.CH": "nestle.com", "NOVN.CH": "novartis.com", "ROG.CH": "roche.com",
  "SHELL.UK": "shell.com", "BP.UK": "bp.com", "AZN.UK": "astrazeneca.com",
  "HSBA.UK": "hsbc.com", "ULVR.UK": "unilever.com", "BARC.UK": "barclays.com",
  "VOD.UK": "vodafone.com", "BATS.UK": "bat.com",
  "ALV.DE": "allianz.com", "BAS.DE": "basf.com", "BMW.DE": "bmw.com",
  "DBK.DE": "db.com", "SIE.DE": "siemens.com", "VOW.DE": "volkswagenag.com",
  "AIR.FR": "airbus.com", "OR.FR": "loreal.com", "TTE.FR": "totalenergies.com",
  "INGA.NL": "ing.com", "PHIA.NL": "philips.com",

  // HK
  "0700.HK": "tencent.com", "9988.HK": "alibabagroup.com",
  "3690.HK": "meituan.com", "9618.HK": "jd.com", "1810.HK": "mi.com",
  "2318.HK": "pingan.cn", "1299.HK": "aia.com", "0941.HK": "chinamobileltd.com",
  "0388.HK": "hkex.com.hk",
  "0001.HK": "ckh.com.hk", "0002.HK": "clpgroup.com", "0003.HK": "towngaschina.com",
  "0011.HK": "hangseng.com", "0016.HK": "shkp.com", "0017.HK": "nwd.com.hk",
  "0066.HK": "mtr.com.hk", "0175.HK": "geelyauto.com.hk",
  "0883.HK": "cnoocltd.com", "0857.HK": "petrochina.com.cn",
  "0939.HK": "ccb.com", "1398.HK": "icbc-ltd.com", "3988.HK": "boc.cn",
  "1088.HK": "shenhuagroup.com.cn",

  // Germany (hub uses suffix DE without a dot)
  ADSDE: "adidas-group.com", ALVDE: "allianz.com", BASDE: "basf.com",
  BAYNDE: "bayer.com", BEIGDE: "beiersdorf.com", BMWDE: "bmw.com",
  BOSSnDE: "hugoboss.com", CBKGDE: "commerzbank.com", CONDE: "continental.com",
  DBKGnDE: "db.com", DPWGnDE: "dhl.com", EONGnDE: "eon.com",
  FREGDE: "fresenius.com", HEIDE: "heidelbergmaterials.com",
  HEN3DE: "henkel.com", IFXGnDE: "infineon.com",
  LHAGDE: "lufthansagroup.com", MTXGnDE: "mtu.de", MUVGnDE: "munichre.com",
  PSHG_pDE: "porsche.com", PUMGDE: "puma.com", RRTLDE: "rtlgroup.com",
  RWEGDE: "rwe.com", SAPDE: "sap.com", SIEDE: "siemens.com",
  VNAnDE: "vonovia.de", VOWG_pDE: "volkswagenag.com", ZALGDE: "zalando.com",
  ENR1DE: "siemens-energy.com", DAIGDE: "mercedes-benz.com",

  // France (hub suffix FR without a dot)
  AIRFFR: "airfranceklm.com", AIRFR: "airbus.com", BNPFR: "bnpparibas.com",
  CAFR: "carrefour.com", CAPPFR: "capgemini.com", AXAFFR: "axa.com",
  SOGNFR: "societegenerale.com", HRMSFR: "hermes.com", TOTFFR: "totalenergies.com",
  LVMHFR: "lvmh.com", OREPFR: "loreal.com",
};

// --- Crypto symbol → atomiclabs slug ---------------------------------------
// Maps the BTCUSD → btc convention; SHIB / PEPE / DOGE etc. all match the
// atomiclabs naming on cryptocurrency-icons@1.0.0.
export function cryptoSlug(symbol) {
  const m = (symbol || "").toUpperCase().match(/^([A-Z0-9]+?)(USD|EUR|USDT|USDC)$/);
  if (!m) return null;
  return m[1].toLowerCase();
}

// --- Public resolver -------------------------------------------------------
// Returns one of:
//   { kind: "crypto",  src }
//   { kind: "stock",   src, alt }
//   { kind: "flagpair", a, b }    // a/b are 2-letter ISO codes
//   { kind: "flag",    cc, alt }
//   { kind: "metal",   metal }    // gold | silver | platinum | palladium
//   { kind: "energy",  energy }   // oil | gas
//   null  → fall through to the lucide category icon
export function resolveSymbolIcon({ symbol, category }) {
  const sym = (symbol || "").toUpperCase();
  const cat = (category || "").toLowerCase();

  if (cat === "crypto") {
    const slug = cryptoSlug(sym);
    if (slug) {
      return {
        kind: "crypto",
        src: `https://cdn.jsdelivr.net/npm/cryptocurrency-icons@0.18.1/svg/color/${slug}.svg`,
      };
    }
  }

  if (cat === "shares") {
    // Hub uses suffix without a dot · "AAPLUS", "MSFTUS", "ADSDE", "BNPFR".
    // We strip the trailing country suffix to look up the company domain.
    let key = sym;
    if (sym.endsWith("US")) key = sym.slice(0, -2);
    else if (sym.endsWith(".HK")) key = sym;          // HK keeps the dot
    else if (sym.endsWith("DE")) key = sym.slice(0, -2);
    else if (sym.endsWith("FR")) key = sym.slice(0, -2);
    else if (sym.endsWith(".US")) key = sym.slice(0, -3);
    const domain = STOCK_DOMAINS[key] || STOCK_DOMAINS[sym];
    if (domain) {
      return {
        kind: "stock",
        // Primary · Clearbit's color brand mark. Falls back to Google's
        // faviconV2 (always returns *something*) and finally lucide.
        src: `https://logo.clearbit.com/${domain}`,
        fallbackSrc: `https://t2.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://${domain}&size=64`,
        alt: key,
      };
    }
  }

  if (cat === "forex" && sym.length === 6) {
    const a = FX_CODE_TO_FLAG[sym.slice(0, 3)];
    const b = FX_CODE_TO_FLAG[sym.slice(3, 6)];
    if (a && b) return { kind: "flagpair", a, b };
  }

  if (cat === "indices") {
    const cc = INDEX_FLAG[sym];
    if (cc) return { kind: "flag", cc, alt: sym };
  }

  if (cat === "metals") {
    if (sym.startsWith("XAU")) return { kind: "metal", metal: "gold" };
    if (sym.startsWith("XAG")) return { kind: "metal", metal: "silver" };
    if (sym.startsWith("XPT")) return { kind: "metal", metal: "platinum" };
    if (sym.startsWith("XPD")) return { kind: "metal", metal: "palladium" };
  }

  if (cat === "energies") {
    if (sym === "NATGAS" || sym === "NGAS") return { kind: "energy", energy: "gas" };
    return { kind: "energy", energy: "oil" };
  }

  return null;
}
